import { ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit, inject, LOCALE_ID } from "@angular/core";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { AngularCsv } from "angular7-csv/dist/Angular-csv";
import { chartBandSpectra } from "app/chart-config/chart-band-spectra";
import { chartBatteryLevel } from "app/chart-config/chart-battery-level";
import { chartBatteryLevel5Minutes } from "app/chart-config/chart-battery-level-5-minutes";
import { chartPowerSupplyStatus } from "app/chart-config/chart-power-supply-status";
import { chartPowerSupplyStatus5Minutes } from "app/chart-config/chart-power-supply-status-5-minutes";
import { chartSoundPressure } from "app/chart-config/chart-sound-pressure";
import { chartSoundPressure5Minutes } from "app/chart-config/chart-sound-pressure-5-minutes";
import { DataTypeModelKey, isRetiredDataTypeModelKey } from "app/models/dataType.model";
import { Data } from "app/models/data.model";
import { DeviceAggregatedData, DeviceDataModel, DeviceDataType } from "app/models/deviceData.model";
import { ProjectDeviceModel } from "app/models/projectDevice.model";
import { DataService } from "app/services/api/data.service";
import * as HighchartsCore from "highcharts";
import * as Highstock from "highcharts/highstock";
import * as moment from "moment";
import { Subscription } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { ProjectDeviceService } from "app/services/api/projectDevice.service";
import { Router } from "@angular/router";
import { MatDatepickerInputEvent } from "@angular/material/datepicker";
import { AlarmService } from "app/services/api/alarm.service";
import { AlarmModel, AlarmModelTimeWindow } from "app/models/alarm.model";
import { EventModelSeverity } from "app/models/event.model";
import { ProjectModel } from "app/models/project.model";
import { ProjectService } from "app/services/api/project.service";
import { MAT_DATE_FORMATS, DateAdapter } from "@angular/material/core";
import { EmmaDateAdapter } from "app/shared/date.adapter";
import { DeviceMqttService } from "app/services/api/device.mqtt.service";
import { IMqttMessage } from "ngx-mqtt";
import { ProjectAlarmService } from "app/services/api/projectAlarm.service";
import { ContextService } from "app/services/context.service";
import { buildAlarmThresholdSeries, buildProjectPeriodPlotBands } from "app/shared/alarm-period-rendering";
import {
    getRenderableSpectrumIndexes,
    hasRenderableSpectrumValues,
    selectSpectrumCategories,
    selectSpectrumSeries,
} from "app/shared/spectrum.util";
import "moment-timezone";
import { chartCellularLevel } from "app/chart-config/chart-cellular-level";
import { chartCellularLevel5Minutes } from "app/chart-config/chart-cellular-level-5-minutes";
import { chartFreeStorage } from "app/chart-config/chart-free-storage";
import { chartFreeStorage5Minutes } from "app/chart-config/chart-free-storage-5-minutes";
import { chartBandSpectraNoAvg } from "app/chart-config/chart-band-spectra-noavg";
import { locale as catalan } from "../../i18n/ca";
import { locale as english } from "../../i18n/en";
import { locale as spanish } from "../../i18n/es";

HighchartsCore.setOptions({
    accessibility: {
        enabled: false,
    },
});

Highstock.setOptions({
    accessibility: {
        enabled: false,
    },
});

type LinkedSpectrumSnapshot = {
    timestamp: number;
    values: Array<number | null | undefined>;
};

@Component({
    selector: "app-device-data",
    templateUrl: "./device-data.component.html",
    styleUrls: ["./device-data.component.scss"],
    providers: [
        { provide: DateAdapter, useClass: EmmaDateAdapter },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: "emma",
                },
                display: {
                    dateInput: "emma",
                    monthYearLabel: "MMM YYYY",
                    dateA11yLabel: "LL",
                    monthYearA11yLabel: "MMMM YYYY",
                },
            },
        },
    ],
    standalone: false,
})
export class DeviceDataComponent implements OnInit, OnDestroy {
    @Input()
    device: ProjectDeviceModel = {
        fields: [],
    } as unknown as ProjectDeviceModel;
    project: ProjectModel;

    startDate = null;
    startTime = "00:00";
    endDate = null;
    endTime = "23:59";
    endDateMin = null;
    endDateMax = null;
    groupingData = "5minutes";
    dateRange = "lasthour";
    liveData = true;
    forceUpdate = true;
    showL10 = true;
    showL90 = true;
    enableLiveData = true;
    enableInstantValues = true;
    loadingData = false;
    exporting = false;
    showLinkedSpectrum = false;

    dataField: DataTypeModelKey;
    sortedFields: DataTypeModelKey[] = [];
    deviceData: DeviceDataModel[];
    alarms: AlarmModel[];
    private deviceAlarms: AlarmModel[] = [];
    private projectAlarms: AlarmModel[] = [];

    //Export fields
    dataFieldExport: DataTypeModelKey;
    startDateExport = null;
    startTimeExport = "00:00";
    endDateExport = null;
    endTimeExport = "23:59";
    groupingDataExport = "no";

    //MQTT Subscription
    subscription: Subscription = null;
    subscriptionBucket: Subscription = null;

    HighchartsCore = HighchartsCore;
    Highstock = Highstock;
    spectrumChart = this.cloneChartConfig(chartBandSpectra);
    chartsConfig = [
        this.cloneChartConfig(chartSoundPressure),
        this.cloneChartConfig(chartSoundPressure5Minutes),
        this.cloneChartConfig(chartBatteryLevel),
        this.cloneChartConfig(chartBatteryLevel5Minutes),
        this.cloneChartConfig(chartPowerSupplyStatus),
        this.cloneChartConfig(chartPowerSupplyStatus5Minutes),
        this.cloneChartConfig(chartBandSpectra),
        this.cloneChartConfig(chartCellularLevel),
        this.cloneChartConfig(chartCellularLevel5Minutes),
        this.cloneChartConfig(chartFreeStorage),
        this.cloneChartConfig(chartFreeStorage5Minutes),
        this.cloneChartConfig(chartBandSpectraNoAvg),
    ];
    chartIndex = 0;
    activeChartConfig = this.chartsConfig[0];
    chartRef: HighchartsCore.Chart | null = null;
    showChart = true;
    linkedSpectrumChart = this.cloneChartConfig(chartBandSpectraNoAvg);
    linkedSpectrumLoading = false;
    linkedSpectrumSnapshots: LinkedSpectrumSnapshot[] = [];
    linkedSpectrumSelectedIndex = 0;
    linkedSpectrumSelectionTimestamp: number | null = null;
    linkedSpectrumSource: DataTypeModelKey | null = null;

    private linkedSpectrumRequestId = 0;
    private linkedSpectrumRangeKey: string | null = null;
    private linkedSpectrumLoadedRangeKey: string | null = null;
    private linkedSpectrumSubscription: Subscription | null = null;
    private scrollToLatestTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private readonly trackedSubscriptions = new Subscription();

    chartCallback: HighchartsCore.ChartCallbackFunction = (chart) => {
        this.chartRef = chart;
    };

    private readonly dataService = inject(DataService);
    private readonly alarmService = inject(AlarmService);
    private readonly projectDeviceService = inject(ProjectDeviceService);
    private readonly projectService = inject(ProjectService);
    private readonly deviceMqtt = inject(DeviceMqttService);
    private readonly projectAlarmService = inject(ProjectAlarmService);
    private readonly contextService = inject(ContextService);
    private readonly toastr = inject(ToastrService);
    private readonly router = inject(Router);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly ngZone = inject(NgZone);
    private readonly translation = inject(FuseTranslationLoaderService);
    locale = inject(LOCALE_ID);

    ngOnInit() {
        if (!this.device?.id) {
            return;
        }
        this.translation.loadTranslations(english, spanish, catalan);
        this.sortedFields = [...(this.device?.fields ?? [])]
            .filter((field) => !isRetiredDataTypeModelKey(field))
            .sort((left, right) =>
                this.translation.translateService
                    .instant(`FIELD.${left}`)
                    .localeCompare(this.translation.translateService.instant(`FIELD.${right}`), this.locale),
            ) as DataTypeModelKey[];
        this.dataField = DataTypeModelKey.soundPressureLevel;
        this.dataFieldExport = DataTypeModelKey.soundPressureLevel;

        // Adjust device frequency gaps
        this.adjustPlotGaps();

        this.loadData();
        this.loadAlarms();

        this.startDate = moment();
        this.updateEndDate(null);

        this.startDateExport = moment().startOf("hour");
        this.endDateExport = moment().startOf("hour");

        this.trackedSubscriptions.add(
            this.projectService.get(this.device.project).subscribe((val) => {
                this.project = val;
                let format = this.project.dateformat;
                if (format) {
                    format = format.toUpperCase();
                    const formats = format.split(" ");
                    EmmaDateAdapter.dateFormat = formats[0];
                }

                // Force date refresh to use the new format
                this.startDateExport = moment(this.startDateExport);
                this.endDateExport = moment(this.endDateExport);
                this.createSensorData();
                this.cdr.detectChanges();
            }),
        );
    }

    ngOnDestroy(): void {
        this.cancelScrollToLatest();
        this.unsubscribeMqttTopics();
        this.linkedSpectrumSubscription?.unsubscribe();
        this.linkedSpectrumSubscription = null;
        this.trackedSubscriptions.unsubscribe();
        this.destroyRenderedChart();
    }

    updateEndDate(event: MatDatepickerInputEvent<Date>) {
        void event;
        this.endDateMin = this.startDate;
        this.endDateMax = moment(this.startDate).add(7, "days");

        if (this.endDateMax.isAfter(moment())) {
            this.endDateMax = moment();
        }

        if (moment(this.endDate).isAfter(this.endDateMax)) {
            this.endDate = this.endDateMax;
        }
    }

    private loadAlarms() {
        this.trackedSubscriptions.add(
            this.alarmService.getAllDevice(this.device.id).subscribe(
                (alarms) => {
                    this.deviceAlarms = alarms ?? [];
                    this.syncLoadedAlarms();
                    this.cdr.detectChanges();
                },
                (error) => {
                    console.log(error);
                    this.toastr.error("Error: " + error.message);
                    this.cdr.detectChanges();
                },
            ),
        );

        const currentProject = this.contextService.currentProject;
        this.trackedSubscriptions.add(
            this.projectAlarmService.getAllProject(currentProject, this.device.id).subscribe(
                (alarms) => {
                    this.projectAlarms = alarms ?? [];
                    this.syncLoadedAlarms();
                    this.cdr.detectChanges();
                },
                (error) => {
                    console.log(error);
                    this.toastr.error("Error: " + error.message);
                    this.cdr.detectChanges();
                },
            ),
        );
    }

    private syncLoadedAlarms() {
        this.alarms = [...this.deviceAlarms, ...this.projectAlarms];
        this.forceUpdate = true;
        this.createSensorData();
    }

    reloadData() {
        this.forceUpdate = true;
        this.loadData();
    }

    reloadGraph() {
        this.forceUpdate = true;
        this.createSensorData();
    }

    get hasLinkedSpectrum(): boolean {
        const hasSpectrum = this.canRenderLinkedSpectrum() && this.getLinkedSpectrumSource() !== null;
        if (!hasSpectrum) {
            this.showLinkedSpectrum = false;
        }
        return hasSpectrum;
    }

    get selectedLinkedSpectrumSnapshot(): LinkedSpectrumSnapshot | null {
        return this.linkedSpectrumSnapshots[this.linkedSpectrumSelectedIndex] ?? null;
    }

    private getGroupedBucketValue(data: DeviceDataModel): number {
        return this.isRollingDataType(this.dataField) ? data.aggregation.max : data.aggregation.average;
    }

    private usesLogarithmicAverage(dataType: DataTypeModelKey): boolean {
        return (
            dataType === DataTypeModelKey.soundPressureLevel ||
            dataType === DataTypeModelKey.soundPressureLevelC ||
            dataType === DataTypeModelKey.soundPressureLevel1Minute ||
            dataType === DataTypeModelKey.soundPressureLevel1MinuteSensor2 ||
            dataType === DataTypeModelKey.soundPressureLevelC1Minute
        );
    }

    private isRollingDataType(dataType: DataTypeModelKey): boolean {
        return (
            dataType === DataTypeModelKey.rolling5s ||
            dataType === DataTypeModelKey.rolling5sC ||
            dataType === DataTypeModelKey.rolling5 ||
            dataType === DataTypeModelKey.rolling5C ||
            dataType === DataTypeModelKey.rolling30 ||
            dataType === DataTypeModelKey.rolling30C
        );
    }

    private usesSoundPressureChart(dataType: DataTypeModelKey): boolean {
        return (
            this.usesLogarithmicAverage(dataType) ||
            dataType === DataTypeModelKey.soundPressureLevelCMinusA ||
            this.isRollingDataType(dataType)
        );
    }

    private getDataUnitLabel(dataType: DataTypeModelKey): string {
        if (
            dataType === DataTypeModelKey.soundPressureLevelC ||
            dataType === DataTypeModelKey.soundPressureLevelC1Minute ||
            dataType === DataTypeModelKey.rolling5sC ||
            dataType === DataTypeModelKey.rolling5C ||
            dataType === DataTypeModelKey.rolling30C
        ) {
            return "dBC";
        }
        if (dataType === DataTypeModelKey.soundPressureLevelCMinusA) {
            return "dB";
        }
        return "dBA";
    }

    private getMainSeriesName(dataType: DataTypeModelKey): string {
        const fieldLabel = this.translation.translateService.instant(`FIELD.${dataType}`);
        if (!this.isRollingDataType(dataType)) {
            return fieldLabel;
        }

        return `${fieldLabel} ${this.translation.translateService.instant("REPORT.YAxisMax").toLowerCase()}`;
    }

    private getPercentileSeriesName(dataType: DataTypeModelKey, percentileLabel: "L10" | "L90"): string {
        const fieldLabel = this.translation.translateService.instant(`FIELD.${dataType}`);
        return `${fieldLabel} (${percentileLabel})`;
    }

    private updateActiveChartTooltipAndSeriesName() {
        if (!this.usesSoundPressureChart(this.dataField)) {
            return;
        }

        const activeChart = this.chartsConfig[this.chartIndex];
        const seriesName = this.getMainSeriesName(this.dataField);
        const l10SeriesName = this.getPercentileSeriesName(this.dataField, "L10");
        const l90SeriesName = this.getPercentileSeriesName(this.dataField, "L90");
        const tooltipOptions = {
            ...activeChart.options.tooltip,
            pointFormat: `{series.name}: <b>{point.y:.1f} ${this.getDataUnitLabel(this.dataField)}</b><br/>`,
        } as HighchartsCore.TooltipOptions;

        activeChart.options.tooltip = tooltipOptions as any;
        activeChart.options.series[0].name = seriesName;
        if (activeChart.options.series[1]) {
            activeChart.options.series[1].name = l10SeriesName;
        }
        if (activeChart.options.series[2]) {
            activeChart.options.series[2].name = l90SeriesName;
        }

        if (this.chartRef?.series?.[0]) {
            this.chartRef.update(
                {
                    tooltip: tooltipOptions,
                },
                false,
            );
            (this.chartRef.series[0] as HighchartsCore.Series).update({ name: seriesName } as HighchartsCore.SeriesOptionsType, false);
            if (this.chartRef.series[1]) {
                (this.chartRef.series[1] as HighchartsCore.Series).update(
                    { name: l10SeriesName } as HighchartsCore.SeriesOptionsType,
                    false,
                );
            }
            if (this.chartRef.series[2]) {
                (this.chartRef.series[2] as HighchartsCore.Series).update(
                    { name: l90SeriesName } as HighchartsCore.SeriesOptionsType,
                    false,
                );
            }
        }
    }

    private isSpectrumDataField(dataType: DataTypeModelKey): boolean {
        return dataType === DataTypeModelKey.spectrum13Octave || dataType === DataTypeModelKey.spectrum13Octave1Minute;
    }

    private canRenderLinkedSpectrum(): boolean {
        return this.dataField === DataTypeModelKey.soundPressureLevel && this.groupingData === "no";
    }

    private getLinkedSpectrumSource(): DataTypeModelKey | null {
        if (this.isSpectrumDataField(this.dataField)) {
            return null;
        }

        if (this.groupData) {
            if (this.device?.fields?.includes(DataTypeModelKey.spectrum13Octave1Minute)) {
                return DataTypeModelKey.spectrum13Octave1Minute;
            }

            return this.device?.fields?.includes(DataTypeModelKey.spectrum13Octave) ? DataTypeModelKey.spectrum13Octave : null;
        }

        if (this.device?.fields?.includes(DataTypeModelKey.spectrum13Octave)) {
            return DataTypeModelKey.spectrum13Octave;
        }

        return this.device?.fields?.includes(DataTypeModelKey.spectrum13Octave1Minute) ? DataTypeModelKey.spectrum13Octave1Minute : null;
    }

    private clearLinkedSpectrumState() {
        this.linkedSpectrumLoading = false;
        this.linkedSpectrumSnapshots = [];
        this.linkedSpectrumSelectedIndex = 0;
        this.linkedSpectrumSelectionTimestamp = null;
        this.linkedSpectrumSource = null;
        this.linkedSpectrumRangeKey = null;
        this.linkedSpectrumLoadedRangeKey = null;
        this.linkedSpectrumChart = this.cloneChartConfig(chartBandSpectraNoAvg);
    }

    private unsubscribeMqttTopics() {
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = null;
        }
        if (this.subscriptionBucket) {
            this.subscriptionBucket.unsubscribe();
            this.subscriptionBucket = null;
        }
    }

    private subscribeToTopic() {
        this.unsubscribeMqttTopics();
        let group = DeviceDataType.instant;
        if (
            this.groupingData === "5minutes" &&
            this.dataField !== DataTypeModelKey.spectrum13Octave &&
            this.dataField !== DataTypeModelKey.spectrum13Octave1Minute &&
            this.dataField !== DataTypeModelKey.battery &&
            this.dataField !== DataTypeModelKey.freeStorage
        ) {
            group = DeviceDataType.bucket;
        }

        //Subscribe to device mqtt messages
        this.subscription = this.deviceMqtt.deviceTopic(this.device.id, group, this.dataField).subscribe((data: IMqttMessage) => {
            this.ngZone.run(() => {
                const item = JSON.parse(data.payload.toString());

                if (this.dataField === DataTypeModelKey.spectrum13Octave || this.dataField === DataTypeModelKey.spectrum13Octave1Minute) {
                    if (this.dataField === DataTypeModelKey.spectrum13Octave) {
                        this.device.lastData.spectrum13Octave = item;
                    } else {
                        this.device.lastData.spectrum13Octave1Minute = item;
                    }

                    // Animate the visible instant spectrum series directly when possible,
                    // and fall back to the full redraw path otherwise.
                    if (this.updateSpectrumInstantSeries()) {
                        return;
                    }

                    this.forceUpdate = true;
                    this.createSensorData();
                    return;
                }

                this.refreshLiveDateWindow();

                //Process message based on the aggregation
                if (this.groupingData === "5minutes") {
                    if (moment(this.deviceData[this.deviceData.length - 1].date).isSame(moment(item.date))) {
                        this.deviceData[this.deviceData.length - 1] = item;
                    } else {
                        this.deviceData.push(item);
                    }

                    //Let's check the total items to keep the date range as it should
                    let max = 0;
                    switch (this.dateRange) {
                        case "last15minutes":
                            max = 4;
                            break;
                        case "lasthour":
                            max = 13;
                            break;
                        case "last4hours":
                            max = 4 * 12 + 1;
                            break;
                        case "last12hours":
                            max = 12 * 12 + 1;
                            break;
                        case "last24hours":
                            max = 24 * 12 + 1;
                            break;
                    }

                    if (this.deviceData.length > max) {
                        this.deviceData.shift();
                    }
                } else {
                    this.upsertInstantPoint(item);
                }

                //Update graph
                this.createSensorData();
            });
        });

        // Spectrum: also subscribe to bucket topic for rolling-average updates
        if (this.dataField === DataTypeModelKey.spectrum13Octave || this.dataField === DataTypeModelKey.spectrum13Octave1Minute) {
            this.subscriptionBucket = this.deviceMqtt
                .deviceTopic(this.device.id, DeviceDataType.bucket, this.dataField)
                .subscribe((data: IMqttMessage) => {
                    this.ngZone.run(() => {
                        const item = JSON.parse(data.payload.toString());
                        if (this.dataField === DataTypeModelKey.spectrum13Octave) {
                            this.device.dataAggregation.spectrum13Octave = item.aggregation;
                        } else {
                            this.device.dataAggregation.spectrum13Octave1Minute = item.aggregation;
                        }
                        this.forceUpdate = true;
                        this.createSensorData();
                    });
                });
        }
    }

    // Insert or replace one instant point by timestamp so historical MQTT repairs redraw correctly.
    private upsertInstantPoint(item: Data<unknown>) {
        if (!item?.timestamp) {
            return;
        }

        const pointTime = new Date(item.timestamp).getTime();
        if (!Number.isFinite(pointTime)) {
            return;
        }

        const bucketDate = moment(pointTime).startOf("minute");
        bucketDate.minutes(Math.floor(bucketDate.minutes() / 5) * 5);
        bucketDate.seconds(0);
        bucketDate.milliseconds(0);
        const bucketTime = bucketDate.valueOf();

        let bucket = this.deviceData.find((entry) => new Date(entry.date).getTime() === bucketTime);
        if (!bucket) {
            bucket = {
                date: bucketDate.toDate(),
                data: [],
            } as DeviceDataModel;
            this.deviceData.push(bucket);
            this.deviceData.sort((d1, d2) => new Date(d1.date).getTime() - new Date(d2.date).getTime());
        }

        if (!Array.isArray(bucket.data)) {
            bucket.data = [];
        }

        const existingIndex = bucket.data.findIndex((point) => new Date(point.timestamp).getTime() === pointTime);
        if (existingIndex >= 0) {
            bucket.data[existingIndex] = item;
        } else {
            bucket.data.push(item);
            bucket.data.sort((d1, d2) => new Date(d1.timestamp).getTime() - new Date(d2.timestamp).getTime());
        }

        this.pruneInstantDataToCurrentWindow();
    }

    private pruneInstantDataToCurrentWindow() {
        if (!this.startDate || !this.deviceData?.length) {
            return;
        }

        const startTime = moment(this.startDate).valueOf();
        if (!Number.isFinite(startTime)) {
            return;
        }

        while (this.deviceData.length > 0) {
            const firstBucket = this.deviceData[0];
            const filtered = (firstBucket.data ?? []).filter((point) => new Date(point.timestamp).getTime() >= startTime);
            if (filtered.length === firstBucket.data?.length) {
                break;
            }
            firstBucket.data = filtered;
            if (firstBucket.data.length > 0) {
                break;
            }
            this.deviceData.shift();
        }
    }

    private refreshLiveDateWindow() {
        if (!this.liveData || !this.enableLiveData || this.dateRange === "custom") {
            return;
        }

        const now = moment();
        switch (this.dateRange) {
            case "last15minutes":
                this.startDate = moment(now).subtract(15, "minutes");
                break;
            case "lasthour":
                this.startDate = moment(now).subtract(1, "hours");
                break;
            case "last4hours":
                this.startDate = moment(now).subtract(4, "hours");
                break;
            default:
                return;
        }
        this.endDate = now;
    }

    loadData() {
        if (this.loadingData) {
            return;
        }
        this.loadingData = true;

        switch (this.dateRange) {
            case "last15minutes":
                this.startDate = moment().subtract(15, "minutes");
                this.endDate = moment();
                this.enableLiveData = true;
                this.enableInstantValues = true;
                break;
            case "lasthour":
                this.startDate = moment().subtract(1, "hours");
                this.endDate = moment();
                this.enableLiveData = true;
                this.enableInstantValues = true;
                break;
            case "last4hours":
                this.startDate = moment().subtract(4, "hours");
                this.endDate = moment();
                this.enableLiveData = true;
                this.enableInstantValues = true;
                break;
            case "last12hours":
                this.startDate = moment().subtract(12, "hours");
                this.endDate = moment();
                this.enableLiveData = false;
                this.enableInstantValues = true;
                break;
            case "last24hours":
                this.startDate = moment().subtract(24, "hours");
                this.endDate = moment();
                this.enableLiveData = false;
                this.enableInstantValues = false;
                if (this.groupingData === "no") {
                    this.groupingData = "5minutes";
                }
                break;
            case "custom": {
                const start = this.startTime.split(":");
                if (start.length !== 2) {
                    this.loadingData = false;
                    return;
                }
                if (!this.startDate) {
                    this.startDate = moment();
                }
                this.startDate.set("hour", start[0]);
                this.startDate.set("minute", start[1]);

                const end = this.endTime.split(":");
                if (end.length !== 2) {
                    this.loadingData = false;
                    return;
                }
                if (!this.endDate) {
                    this.endDate = moment();
                }
                this.endDate.set("hour", end[0]);
                this.endDate.set("minute", end[1]);

                this.enableLiveData = false;
                this.enableInstantValues = true;
                if (this.groupingData === "no" && this.endDate.diff(this.startDate, "hours") > 4) {
                    this.groupingData = "5minutes";
                }
                if (this.endDate.diff(this.startDate, "hours") > 4) {
                    this.enableInstantValues = false;
                }
                break;
            }
        }
        if (this.enableLiveData === false) {
            this.liveData = false;
        }

        if (this.forceUpdate === false && (!this.enableLiveData || !this.liveData)) {
            this.loadingData = false;
            return;
        }

        if (!this.startDate || !this.endDate) {
            this.loadingData = false;
            return;
        }

        //Spectrum is a special case of graph, we only need the last item, so we query 10 seconds and we get the last one
        if (this.dataField === DataTypeModelKey.spectrum13Octave) {
            this.startDate = moment().subtract(10, "seconds");
            this.endDate = moment();

            // Fetch bucket of data of last hour (12 buckets)
            this.trackedSubscriptions.add(
                this.projectDeviceService.get(this.device.id).subscribe(
                    (data: ProjectDeviceModel) => {
                        this.device.lastData = data.lastData;
                        this.device.dataAggregation = data.dataAggregation;
                        this.createSensorData();
                        this.loadingData = false;
                        this.cdr.detectChanges();

                        //Subscribe to MQTT message to update the graph
                        this.subscribeToTopic();
                    },
                    (error) => {
                        console.log(error);
                        this.toastr.error("Error: " + error.message);
                        this.loadingData = false;
                        this.cdr.detectChanges();
                    },
                ),
            );
            return;
        }

        this.trackedSubscriptions.add(
            this.dataService.getDeviceDataRange(this.device.id, [this.dataField], this.startDate, this.endDate, this.groupData).subscribe(
                (data) => {
                    this.deviceData = data[this.dataField];
                    // From API data are coming from the most recent to the latest, reverting the order.
                    this.deviceData.sort((d1, d2) => new Date(d1.date).getTime() - new Date(d2.date).getTime());
                    //Draw the graph
                    this.createSensorData();
                    this.loadingData = false;
                    this.cdr.detectChanges();

                    //Subscribe to MQTT message to update the graph
                    this.subscribeToTopic();
                },
                (error) => {
                    console.log(error);
                    this.toastr.error("Error: " + error.message);
                    if (error.error.statusCode === 404) {
                        this.router.navigate(["/devices"]);
                    }
                    this.loadingData = false;
                    this.cdr.detectChanges();
                },
            ),
        );
    }

    //Get spectrum values as an ordered array.
    private getSpectrumValues(value: unknown): number[] {
        return Array.isArray(value) ? value : [];
    }

    //Check whether the spectrum chart has average data.
    private hasSpectrumAverage(): boolean {
        //Read average values safely.
        const averageSpectrum =
            this.dataField === DataTypeModelKey.spectrum13Octave1Minute
                ? this.device?.dataAggregation?.spectrum13Octave1Minute?.average
                : this.device?.dataAggregation?.spectrum13Octave?.average;
        return hasRenderableSpectrumValues(averageSpectrum);
    }

    //Build a fresh spectrum chart config from current device data.
    private buildSpectrumChartConfig() {
        //Pick the right base chart.
        const hasAverageSpectrum = this.hasSpectrumAverage();
        const spectrumChart = this.cloneChartConfig(hasAverageSpectrum ? chartBandSpectra : chartBandSpectraNoAvg);
        const spectrumLength = hasAverageSpectrum ? 34 : 31;
        const spectrumValues = this.getSpectrumValues(
            this.dataField === DataTypeModelKey.spectrum13Octave1Minute
                ? this.device?.lastData?.spectrum13Octave1Minute?.value
                : this.device?.lastData?.spectrum13Octave?.value,
        );
        const averageSpectrum =
            this.dataField === DataTypeModelKey.spectrum13Octave1Minute
                ? this.device?.dataAggregation?.spectrum13Octave1Minute?.average
                : this.device?.dataAggregation?.spectrum13Octave?.average;
        const referenceSpectrumValues = hasAverageSpectrum ? averageSpectrum : spectrumValues;
        const spectrumIndexes = getRenderableSpectrumIndexes(this.device?.baseDeviceData?.model, referenceSpectrumValues, spectrumLength);
        const categories = selectSpectrumCategories([...(spectrumChart.options.xAxis.categories ?? [])], spectrumIndexes);
        spectrumChart.options.xAxis.categories = categories;

        //Fill the instant-only spectrum.
        if (!hasAverageSpectrum) {
            spectrumChart.options.series[0].data.push(
                ...selectSpectrumSeries(
                    spectrumValues.map((data) => Math.round(data * 10) / 10),
                    spectrumIndexes,
                ),
            );
            return spectrumChart;
        }

        //Fill the average spectrum series.
        const averageSeries = (averageSpectrum ?? []).map((data, i) => {
            const rounded = Math.round(data * 10) / 10;
            if (i > 30) {
                return {
                    y: rounded,
                    color: "#0C65E8",
                };
            }
            return rounded;
        });

        //Fill the instant spectrum series.
        const instantSeries = spectrumValues.map((data, i) => {
            const rounded = Math.round(data * 10) / 10;
            if (i > 30) {
                return {
                    y: rounded,
                    color: "#0C65E877",
                };
            }
            return rounded;
        });
        spectrumChart.options.series[0].data.push(...selectSpectrumSeries(averageSeries, spectrumIndexes));
        spectrumChart.options.series[1].data.push(...selectSpectrumSeries(instantSeries, spectrumIndexes));

        return spectrumChart;
    }

    //Update the instant spectrum series directly when possible.
    private updateSpectrumInstantSeries(): boolean {
        //Skip non-spectrum charts.
        if (
            (this.dataField !== DataTypeModelKey.spectrum13Octave && this.dataField !== DataTypeModelKey.spectrum13Octave1Minute) ||
            !this.chartRef
        ) {
            return false;
        }

        //Resolve the visible series.
        const hasAverageSpectrum = this.hasSpectrumAverage();
        const spectrumValues = this.getSpectrumValues(
            this.dataField === DataTypeModelKey.spectrum13Octave1Minute
                ? this.device?.lastData?.spectrum13Octave1Minute?.value
                : this.device?.lastData?.spectrum13Octave?.value,
        );
        const seriesIndex = hasAverageSpectrum ? 1 : 0;
        const series = this.chartRef.series?.[seriesIndex];

        //Fallback to a full redraw if the series is missing.
        if (!series) {
            return false;
        }

        //Build the next point list.
        const nextData = spectrumValues.map((data, i) => {
            const rounded = Math.round(data * 10) / 10;
            if (hasAverageSpectrum && i > 30) {
                return {
                    y: rounded,
                    color: "#0C65E877",
                };
            }

            return rounded;
        });

        //Fallback to full redraw if the point count changed.
        if (series.points.length !== nextData.length) {
            return false;
        }

        //Apply the animated update.
        series.setData(
            nextData as unknown as HighchartsCore.PointOptionsType[],
            true,
            {
                duration: 250,
            },
            true,
        );
        return true;
    }

    //Apply animated updates to all rendered spectrum series.
    private updateSpectrumSeries(): boolean {
        //Skip non-spectrum charts.
        if (
            (this.dataField !== DataTypeModelKey.spectrum13Octave && this.dataField !== DataTypeModelKey.spectrum13Octave1Minute) ||
            !this.chartRef
        ) {
            return false;
        }

        //Get next series data from the prepared chart config.
        const nextSeries = this.spectrumChart?.options?.series;
        if (!Array.isArray(nextSeries) || nextSeries.length === 0) {
            return false;
        }

        //Validate rendered series before applying updates.
        const renderedSeries = this.chartRef.series ?? [];
        if (renderedSeries.length < nextSeries.length) {
            return false;
        }

        //Update each series with animation and redraw once.
        nextSeries.forEach((series, index) => {
            renderedSeries[index].setData(
                (series?.data ?? []) as unknown as HighchartsCore.PointOptionsType[],
                false,
                {
                    duration: 250,
                },
                true,
            );
        });
        this.chartRef.redraw();
        return true;
    }

    private updateRenderedChart(previousChartIndex: number) {
        if (!this.chartRef || previousChartIndex !== this.chartIndex) {
            if (this.dataField === DataTypeModelKey.spectrum13Octave || this.dataField === DataTypeModelKey.spectrum13Octave1Minute) {
                this.spectrumChart.update = true;
            } else {
                this.activeChartConfig.update = true;
            }
            this.cdr.detectChanges();
            return;
        }

        if (this.dataField === DataTypeModelKey.spectrum13Octave || this.dataField === DataTypeModelKey.spectrum13Octave1Minute) {
            //Use direct series updates to preserve animated transitions.
            if (this.updateSpectrumSeries()) {
                return;
            }

            //Fallback to full update when series are not ready.
            this.chartRef.update(this.spectrumChart.options as unknown as HighchartsCore.Options, true, true, {
                duration: 250,
            });
            return;
        }

        const renderedAlarmSeries = this.getAlarmThresholdSeries(
            (this.chartRef.series ?? []).map((series) => ({
                id: (series.options as { id?: string } | undefined)?.id,
                data: (series.options as { data?: Array<[number, number]> } | undefined)?.data,
            })),
        );
        const nextAlarmSeries = this.getAlarmThresholdSeries(
            this.activeChartConfig?.options?.series as Array<{ id?: string; data?: Array<[number, number]> }> | undefined,
        );
        const renderedAlarmSeriesIds = renderedAlarmSeries.map((series) => series.id);
        const nextAlarmSeriesIds = nextAlarmSeries.map((series) => series.id);

        if (
            renderedAlarmSeriesIds.length !== nextAlarmSeriesIds.length ||
            renderedAlarmSeriesIds.some((id, index) => id !== nextAlarmSeriesIds[index])
        ) {
            this.activeChartConfig.update = true;
            this.cdr.detectChanges();
            return;
        }

        // Use direct Highcharts updates for live MQTT refreshes because the Angular
        // update flag can miss rapid consecutive updates. Pass an explicit animation
        // option so line charts keep the smooth transition behavior.
        this.chartRef.update(this.activeChartConfig.options as unknown as HighchartsCore.Options, true, true, {
            duration: 250,
        });

        nextAlarmSeries.forEach((seriesOptions) => {
            const renderedSeries = this.chartRef?.get(seriesOptions.id) as HighchartsCore.Series | undefined;
            if (!renderedSeries) {
                return;
            }

            const renderedSeriesData = this.serializeAlarmThresholdData(
                ((renderedSeries.options as { data?: Array<[number, number]> } | undefined)?.data ?? []) as Array<[number, number]>,
            );
            const nextSeriesData = this.serializeAlarmThresholdData((seriesOptions.data ?? []) as Array<[number, number]>);

            if (renderedSeriesData === nextSeriesData) {
                return;
            }

            renderedSeries.setData(
                (seriesOptions.data ?? []) as unknown as HighchartsCore.PointOptionsType[],
                false,
                {
                    duration: 250,
                },
                true,
            );
        });
        this.chartRef.redraw();
    }

    private attachLinkedSpectrumInteractions() {
        if (this.isSpectrumDataField(this.dataField) || !this.chartsConfig[this.chartIndex]?.options) {
            return;
        }

        const selectLinkedSpectrum = (timestamp: number) => this.selectLinkedSpectrumByTimestamp(timestamp);
        const selectionOffset = this.groupData ? 2.5 * 60 * 1000 : 0;
        const options = this.chartsConfig[this.chartIndex].options as unknown as HighchartsCore.Options & {
            plotOptions?: {
                series?: {
                    point?: {
                        events?: {
                            mouseOver?: (this: HighchartsCore.Point) => void;
                        };
                    };
                };
            };
        };
        const seriesPlotOptions = (options.plotOptions?.series ?? {}) as {
            point?: {
                events?: {
                    mouseOver?: (this: HighchartsCore.Point) => void;
                };
            };
        };
        const pointOptions = seriesPlotOptions.point ?? {};
        pointOptions.events = {
            ...(pointOptions.events ?? {}),
            mouseOver(this: HighchartsCore.Point) {
                selectLinkedSpectrum(this.x + selectionOffset);
            },
        };
        seriesPlotOptions.point = pointOptions;
        options.plotOptions = {
            ...(options.plotOptions ?? {}),
            series: {
                ...(options.plotOptions?.series ?? {}),
                ...seriesPlotOptions,
            },
        };

        const xAxisOptions = Array.isArray(options.xAxis) ? options.xAxis[0] : options.xAxis;
        if (!xAxisOptions) {
            return;
        }

        xAxisOptions.events = {
            ...(xAxisOptions.events ?? {}),
            afterSetExtremes: (event) => {
                if (typeof event.min !== "number" || typeof event.max !== "number") {
                    return;
                }

                this.loadLinkedSpectrumRange(new Date(event.min), new Date(event.max));
            },
        };
    }

    //Resolve the chart index for the current field and grouping.
    private resolveChartIndex() {
        //Set the base chart index for the selected field.
        switch (this.dataField) {
            case DataTypeModelKey.soundPressureLevel:
            case DataTypeModelKey.soundPressureLevelC:
            case DataTypeModelKey.soundPressureLevel1Minute:
            case DataTypeModelKey.soundPressureLevel1MinuteSensor2:
            case DataTypeModelKey.soundPressureLevelC1Minute:
            case DataTypeModelKey.soundPressureLevelCMinusA:
            case DataTypeModelKey.rolling5s:
            case DataTypeModelKey.rolling5sC:
            case DataTypeModelKey.rolling5:
            case DataTypeModelKey.rolling5C:
            case DataTypeModelKey.rolling30:
            case DataTypeModelKey.rolling30C:
                this.chartIndex = 0;
                break;
            case DataTypeModelKey.battery:
                this.chartIndex = 2;
                break;
            case DataTypeModelKey.powerSupplyStatus:
                this.chartIndex = 4;
                break;
            case DataTypeModelKey.spectrum13Octave:
            case DataTypeModelKey.spectrum13Octave1Minute: {
                this.chartIndex = 6;
                const averageSpectrum =
                    this.dataField === DataTypeModelKey.spectrum13Octave1Minute
                        ? this.device?.dataAggregation?.spectrum13Octave1Minute?.average
                        : this.device?.dataAggregation?.spectrum13Octave?.average;
                if (!hasRenderableSpectrumValues(averageSpectrum)) {
                    this.chartIndex = 11;
                }
                break;
            }
            case DataTypeModelKey.cellullarSignalLevel:
                this.chartIndex = 7;
                break;
            case DataTypeModelKey.freeStorage:
                this.chartIndex = 9;
                break;
        }

        //Apply grouped variant index for non-spectrum series.
        if (
            this.groupData &&
            this.dataField !== DataTypeModelKey.spectrum13Octave &&
            this.dataField !== DataTypeModelKey.spectrum13Octave1Minute
        ) {
            this.chartIndex++;
        }
    }

    //Get alarms matching the active field and time window.
    private getActiveAlarms(): AlarmModel[] {
        if (!this.alarms?.length) {
            return [];
        }

        const preferredWindows =
            this.groupingData === "no"
                ? [AlarmModelTimeWindow.AlarmModelTimeWindowInstant]
                : this.groupingData === "5minutes"
                  ? [AlarmModelTimeWindow.AlarmModelTimeWindowBucket5]
                  : [];

        return this.alarms.filter((alarm) => alarm.field === this.dataField && preferredWindows.includes(alarm.window));
    }

    //Toggle visible chart config based on active index.
    private updateChartVisibility() {
        //Show only the active chart configuration.
        this.chartsConfig.forEach((element, key) => {
            element.visible = key === this.chartIndex;
        });
    }

    //Populate non-spectrum chart series with current data points.
    private populateNonSpectrumSeries() {
        this.updateActiveChartTooltipAndSeriesName();
        this.stripAlarmThresholdSeries();

        //Reset current chart series.
        this.chartsConfig[this.chartIndex].options.series.forEach((element) => {
            element.data = [];
        });

        //Fill chart points from device payload.
        this.deviceData.forEach((data) => {
            //Push grouped averages.
            if (this.groupData) {
                if (!data.aggregation) {
                    return;
                }
                this.chartsConfig[this.chartIndex].options.series[0].data.push([
                    new Date(data.date).getTime(),
                    Math.round(this.getGroupedBucketValue(data) * 10) / 10,
                ]);

                //Push percentile series when available.
                if (this.usesLogarithmicAverage(this.dataField) && this.percentilesAvailable) {
                    this.chartsConfig[this.chartIndex].options.series[1].data.push([
                        new Date(data.date).getTime(),
                        Math.round(data.aggregation.percentile90 * 10) / 10,
                    ]);
                    this.chartsConfig[this.chartIndex].options.series[2].data.push([
                        new Date(data.date).getTime(),
                        Math.round(data.aggregation.percentile10 * 10) / 10,
                    ]);
                }
                return;
            }

            //Push instant values in timestamp order.
            const pointData = data.data ? data.data : [];
            pointData.sort((d1, d2) => new Date(d1.timestamp).getTime() - new Date(d2.timestamp).getTime());
            pointData.forEach((point) => {
                this.chartsConfig[this.chartIndex].options.series[0].data.push([
                    new Date(point.timestamp).getTime(),
                    Math.round(point.value * 10) / 10,
                ]);
            });
        });
    }

    //Apply L10/L90 visibility based on current grouping and data completeness.
    private updatePercentileVisibility() {
        if (this.chartsConfig[this.chartIndex].options.series.length < 3) {
            return;
        }

        //Skip non sound-pressure grouped charts.
        if (!this.usesLogarithmicAverage(this.dataField) || !this.groupData) {
            this.chartsConfig[this.chartIndex].options.series[1].visible = false;
            this.chartsConfig[this.chartIndex].options.series[2].visible = false;
            return;
        }

        //Set series visibility based on selected controls.
        if (this.groupingData === "5minutes" && this.percentilesAvailable) {
            this.chartsConfig[this.chartIndex].options.series[1].visible = this.showL10;
            this.chartsConfig[this.chartIndex].options.series[2].visible = this.showL90;
            return;
        }

        this.chartsConfig[this.chartIndex].options.series[1].visible = false;
        this.chartsConfig[this.chartIndex].options.series[2].visible = false;
    }

    //Apply alarm threshold plot lines to the active chart.
    private updateAlarmPlotLines(activeAlarms: AlarmModel[]) {
        this.chartsConfig[this.chartIndex].options.yAxis[0].plotLines.length = 0;
        if (!activeAlarms?.length) {
            return;
        }

        const [rangeStart, rangeEnd] = this.getCurrentChartRange();
        const alarmSeries = this.buildChartAlarmSeries(activeAlarms, rangeStart, rangeEnd);

        (this.chartsConfig[this.chartIndex].options.series as any[]).push(...alarmSeries);
    }

    private buildChartAlarmSeries(
        activeAlarms: AlarmModel[] | null | undefined,
        rangeStart: Date | moment.Moment | null | undefined,
        rangeEnd: Date | moment.Moment | null | undefined,
    ): any[] {
        if (!activeAlarms?.length) {
            return [];
        }

        const alarmThresholdOptions = {
            projectDeviceId: this.device?.id,
            lf010Settings: this.device?.settings?.lf010,
        };

        return activeAlarms.map((alarm) => ({
            id: `alarm-threshold-${alarm.id}`,
            type: "line",
            name: alarm.name,
            data: buildAlarmThresholdSeries(alarm, this.project, rangeStart, rangeEnd, alarmThresholdOptions),
            color: alarm.severity === EventModelSeverity.EventModelSeverityAlarm ? "#dc2626" : "#f59e0b",
            dashStyle: "ShortDash",
            lineWidth: 2,
            step: "left",
            gapSize: 0,
            zIndex: 9,
            visible: true,
            marker: {
                enabled: false,
            },
            states: {
                hover: {
                    enabled: false,
                },
            },
            dataLabels: {
                enabled: true,
                allowOverlap: true,
                crop: false,
                overflow: "allow",
                align: "left",
                x: 3,
                y: -2,
                formatter: function () {
                    const points = this.series.points ?? [];
                    const firstPoint = points[0];
                    if (firstPoint !== this.point) {
                        return "";
                    }

                    return `${this.series.name}: ${this.y}`;
                },
                style: {
                    color: alarm.severity === EventModelSeverity.EventModelSeverityAlarm ? "#dc2626" : "#f59e0b",
                    fontSize: "11px",
                    fontWeight: "600",
                    textOutline: "none",
                },
            },
            showInNavigator: false,
            enableMouseTracking: false,
        }));
    }

    private updateProjectPeriodBands() {
        const [rangeStart, rangeEnd] = this.getCurrentChartRange();
        const xAxis = this.chartsConfig[this.chartIndex].options.xAxis as {
            plotBands?: any[];
            plotLines?: any[];
        };
        xAxis.plotBands = buildProjectPeriodPlotBands(this.project, rangeStart, rangeEnd);
        xAxis.plotLines = [];
    }

    private getCurrentChartRange(): [Date | moment.Moment | null, Date | moment.Moment | null] {
        const primarySeries = this.chartsConfig[this.chartIndex].options.series?.[0]?.data ?? [];
        if (primarySeries.length) {
            const timestamps = primarySeries
                .map((point) => (Array.isArray(point) ? point[0] : point?.x))
                .filter((timestamp): timestamp is number => typeof timestamp === "number");

            if (timestamps.length) {
                const minTimestamp = Math.min(...timestamps);
                const maxTimestamp = Math.max(...timestamps);

                if (Number.isFinite(minTimestamp) && Number.isFinite(maxTimestamp)) {
                    if (maxTimestamp > minTimestamp) {
                        return [new Date(minTimestamp), new Date(maxTimestamp)];
                    }

                    const extremes = this.chartRef?.xAxis?.[0]?.getExtremes();
                    if (typeof extremes?.min === "number" && typeof extremes?.max === "number" && extremes.max > extremes.min) {
                        return [new Date(extremes.min), new Date(extremes.max)];
                    }
                }
            }
        }

        return [this.startDate, this.endDate];
    }

    private stripAlarmThresholdSeries() {
        this.chartsConfig[this.chartIndex].options.series = (this.chartsConfig[this.chartIndex].options.series as any[]).filter(
            (series) => !String(series.id ?? "").startsWith("alarm-threshold-"),
        );
    }

    private getAlarmThresholdSeries(
        series: Array<{ id?: string; data?: Array<[number, number]> }> | undefined | null,
    ): Array<{ id: string; data?: Array<[number, number]> }> {
        return (series ?? [])
            .map((entry) => ({
                id: String(entry?.id ?? ""),
                data: entry?.data,
            }))
            .filter((entry) => entry.id.startsWith("alarm-threshold-"))
            .sort((left, right) => left.id.localeCompare(right.id));
    }

    private serializeAlarmThresholdData(data: Array<[number, number]> | undefined | null): string {
        return (data ?? []).map((point) => `${point[0]}:${point[1]}`).join("|");
    }

    //Populate spectrum chart state and remount when series shape changes.
    private populateSpectrumChart() {
        //Build the next spectrum chart from fresh config.
        const nextSpectrumChart = this.buildSpectrumChartConfig();
        const spectrumSeriesChanged = this.spectrumChart?.options?.series?.length !== nextSpectrumChart.options.series.length;
        const spectrumCategoriesChanged =
            this.spectrumChart?.options?.xAxis?.categories?.length !== nextSpectrumChart.options.xAxis.categories.length;
        this.spectrumChart = nextSpectrumChart;

        //Recreate the chart when the spectrum mode changes.
        if (spectrumSeriesChanged || spectrumCategoriesChanged) {
            this.recreateChart();
        }
    }

    private syncLinkedSpectrumContext() {
        if (!this.canRenderLinkedSpectrum() || !this.showLinkedSpectrum) {
            this.clearLinkedSpectrumState();
            return;
        }

        this.linkedSpectrumSource = this.getLinkedSpectrumSource();
        if (!this.linkedSpectrumSource) {
            this.clearLinkedSpectrumState();
            return;
        }

        const [rangeStart, rangeEnd] = this.getCurrentLinkedSpectrumRange();
        this.loadLinkedSpectrumRange(rangeStart, rangeEnd);
    }

    private loadLinkedSpectrumRange(rangeStart: Date | moment.Moment | null, rangeEnd: Date | moment.Moment | null) {
        if (!this.linkedSpectrumSource || !rangeStart || !rangeEnd) {
            return;
        }

        const normalizedRange = this.normalizeLinkedSpectrumRange(rangeStart, rangeEnd);
        if (!normalizedRange) {
            return;
        }

        const { start, end, key } = normalizedRange;
        if (key === this.linkedSpectrumRangeKey && (this.linkedSpectrumLoading || key === this.linkedSpectrumLoadedRangeKey)) {
            return;
        }

        this.linkedSpectrumRangeKey = key;
        this.linkedSpectrumLoadedRangeKey = null;
        this.linkedSpectrumLoading = true;
        const requestId = ++this.linkedSpectrumRequestId;
        this.linkedSpectrumSubscription?.unsubscribe();

        this.linkedSpectrumSubscription = this.dataService
            .getDeviceDataRange(this.device.id, [this.linkedSpectrumSource], start, end, false)
            .subscribe(
                (data: DeviceAggregatedData) => {
                    if (requestId !== this.linkedSpectrumRequestId) {
                        return;
                    }

                    this.linkedSpectrumSnapshots = this.buildLinkedSpectrumSnapshots(data[this.linkedSpectrumSource] ?? []);
                    this.linkedSpectrumLoading = false;
                    this.linkedSpectrumLoadedRangeKey = key;
                    this.selectLinkedSpectrumAfterReload(this.linkedSpectrumSelectionTimestamp);
                    this.rebuildLinkedSpectrumChart();
                    this.cdr.detectChanges();
                },
                (error) => {
                    if (requestId !== this.linkedSpectrumRequestId) {
                        return;
                    }

                    console.log(error);
                    this.linkedSpectrumLoading = false;
                    this.linkedSpectrumSnapshots = [];
                    this.linkedSpectrumSelectedIndex = 0;
                    this.linkedSpectrumRangeKey = null;
                    this.linkedSpectrumLoadedRangeKey = null;
                    this.rebuildLinkedSpectrumChart();
                    this.cdr.detectChanges();
                },
            );
    }

    private getCurrentLinkedSpectrumRange(): [Date | moment.Moment | null, Date | moment.Moment | null] {
        const extremes = this.chartRef?.xAxis?.[0]?.getExtremes();
        if (typeof extremes?.min === "number" && typeof extremes?.max === "number" && extremes.max > extremes.min) {
            return [new Date(extremes.min), new Date(extremes.max)];
        }

        return this.getCurrentChartRange();
    }

    private normalizeLinkedSpectrumRange(
        rangeStart: Date | moment.Moment,
        rangeEnd: Date | moment.Moment,
    ): { end: Date; key: string; start: Date } | null {
        const startMillis = moment(rangeStart).valueOf();
        const endMillis = moment(rangeEnd).valueOf();
        if (!Number.isFinite(startMillis) || !Number.isFinite(endMillis) || endMillis <= startMillis) {
            return null;
        }

        const roundingStep = this.linkedSpectrumSource === DataTypeModelKey.spectrum13Octave1Minute ? 60 * 1000 : 10 * 1000;
        const start = new Date(Math.floor(startMillis / roundingStep) * roundingStep);
        const end = new Date(Math.ceil(endMillis / roundingStep) * roundingStep);

        return {
            start,
            end,
            key: `${this.linkedSpectrumSource}:${start.toISOString()}:${end.toISOString()}`,
        };
    }

    private buildLinkedSpectrumSnapshots(deviceData: DeviceDataModel[]): LinkedSpectrumSnapshot[] {
        const snapshots = deviceData
            .flatMap((bucket) => {
                if (bucket.data?.length) {
                    return bucket.data.map((point) => ({
                        timestamp: new Date(point.timestamp).getTime(),
                        values: this.getSpectrumValues(point.value).map((value) =>
                            Number.isFinite(value) ? Math.round(value * 10) / 10 : value,
                        ),
                    }));
                }

                if (hasRenderableSpectrumValues(bucket.aggregation?.average)) {
                    return [
                        {
                            timestamp: new Date(bucket.date).getTime(),
                            values: this.getSpectrumValues(bucket.aggregation.average).map((value) =>
                                Number.isFinite(value) ? Math.round(value * 10) / 10 : value,
                            ),
                        },
                    ];
                }

                return [];
            })
            .filter((point) => Number.isFinite(point.timestamp) && point.values.length > 0)
            .sort((left, right) => left.timestamp - right.timestamp);

        const deduplicated = new Map<number, LinkedSpectrumSnapshot>();
        snapshots.forEach((snapshot) => {
            deduplicated.set(snapshot.timestamp, snapshot);
        });

        return Array.from(deduplicated.values());
    }

    private selectLinkedSpectrumByTimestamp(timestamp: number | null) {
        if (!this.linkedSpectrumSnapshots.length) {
            this.linkedSpectrumSelectionTimestamp = timestamp;
            return;
        }

        if (typeof timestamp !== "number" || Number.isNaN(timestamp)) {
            this.linkedSpectrumSelectedIndex = this.linkedSpectrumSnapshots.length - 1;
        } else {
            this.linkedSpectrumSelectedIndex = this.findClosestLinkedSpectrumIndex(timestamp);
        }
        this.linkedSpectrumSelectionTimestamp = timestamp;
        this.rebuildLinkedSpectrumChart();
        this.cdr.detectChanges();
    }

    private findClosestLinkedSpectrumIndex(timestamp: number): number {
        return this.linkedSpectrumSnapshots.reduce((closestIndex, snapshot, index, snapshots) => {
            const closestDelta = Math.abs(snapshots[closestIndex].timestamp - timestamp);
            const nextDelta = Math.abs(snapshot.timestamp - timestamp);
            return nextDelta < closestDelta ? index : closestIndex;
        }, 0);
    }

    private getRenderableSpectrumSnapshot(values: number[]): {
        categories: string[];
        values: number[];
    } {
        const totalLength = values.length > 31 ? 34 : 31;
        const spectrumIndexes = getRenderableSpectrumIndexes(this.device?.baseDeviceData?.model, values, totalLength);
        const categories = selectSpectrumCategories(
            [...((values.length > 31 ? chartBandSpectra : chartBandSpectraNoAvg).options.xAxis.categories ?? [])],
            spectrumIndexes,
        );

        return {
            categories,
            values: selectSpectrumSeries(values, spectrumIndexes).map((value) => Math.round(value * 10) / 10),
        };
    }

    private rebuildLinkedSpectrumChart() {
        const nextChart = this.cloneChartConfig(chartBandSpectraNoAvg);
        const snapshot = this.selectedLinkedSpectrumSnapshot;
        if (!snapshot) {
            this.linkedSpectrumChart = nextChart;
            return;
        }

        const renderableSnapshot = this.getRenderableSpectrumSnapshot(snapshot.values);
        nextChart.options.xAxis.categories = renderableSnapshot.categories;
        nextChart.options.chart.height = 360;
        nextChart.options.series[0].data = renderableSnapshot.values;
        this.linkedSpectrumChart = nextChart;
        this.linkedSpectrumChart.update = true;
    }

    private selectLinkedSpectrumAfterReload(timestamp: number | null) {
        if (!this.linkedSpectrumSnapshots.length) {
            this.linkedSpectrumSelectedIndex = 0;
            return;
        }

        if (typeof timestamp !== "number" || Number.isNaN(timestamp)) {
            this.linkedSpectrumSelectedIndex = this.linkedSpectrumSnapshots.length - 1;
            this.linkedSpectrumSelectionTimestamp = this.linkedSpectrumSnapshots[this.linkedSpectrumSelectedIndex].timestamp;
            return;
        }

        this.linkedSpectrumSelectedIndex = this.findClosestLinkedSpectrumIndex(timestamp);
        this.linkedSpectrumSelectionTimestamp = this.linkedSpectrumSnapshots[this.linkedSpectrumSelectedIndex].timestamp;
    }

    onLinkedSpectrumIndexChange(value: string) {
        const index = Number(value);
        if (!Number.isInteger(index) || index < 0 || index >= this.linkedSpectrumSnapshots.length) {
            return;
        }

        this.linkedSpectrumSelectedIndex = index;
        this.linkedSpectrumSelectionTimestamp = this.linkedSpectrumSnapshots[index].timestamp;
        this.rebuildLinkedSpectrumChart();
    }

    private formatLinkedSpectrumTimestamp(timestamp: number): string {
        if (!Number.isFinite(timestamp)) {
            return "";
        }

        const timezone = this.project?.timezone;
        const zoned = timezone ? moment(timestamp).tz(timezone) : moment(timestamp);
        return zoned.format(this.project.dateformat);
    }

    //Build chart series and refresh the rendered chart.
    createSensorData() {
        //Keep references needed after the chart update.
        const previousChartIndex = this.chartIndex;
        const shouldFollowLatest = this.shouldFollowLatestPoint();
        const visibleRange = this.getVisibleRange();

        // Spectrum chart uses device.lastData / dataAggregation only; deviceData is never set for spectrum load
        if (
            !this.deviceData &&
            this.dataField !== DataTypeModelKey.spectrum13Octave &&
            this.dataField !== DataTypeModelKey.spectrum13Octave1Minute
        ) {
            return;
        }

        if (!this.forceUpdate) {
            if (!this.liveData) {
                return;
            }
        }

        this.forceUpdate = false;
        this.resolveChartIndex();
        this.updateChartVisibility();

        //Build spectrum or regular time-series data.
        if (this.dataField === DataTypeModelKey.spectrum13Octave || this.dataField === DataTypeModelKey.spectrum13Octave1Minute) {
            this.populateSpectrumChart();
            this.clearLinkedSpectrumState();
        } else {
            const activeAlarms = this.getActiveAlarms();
            this.populateNonSpectrumSeries();
            this.updateProjectPeriodBands();
            this.updatePercentileVisibility();
            this.updateAlarmPlotLines(activeAlarms);
            this.attachLinkedSpectrumInteractions();
            this.syncLinkedSpectrumContext();
        }

        // Force graph update
        if (previousChartIndex !== this.chartIndex) {
            this.recreateChart();
        }

        if (this.dataField !== DataTypeModelKey.spectrum13Octave && this.dataField !== DataTypeModelKey.spectrum13Octave1Minute) {
            this.activeChartConfig = this.chartsConfig[this.chartIndex];
        }
        this.updateRenderedChart(previousChartIndex);

        if (shouldFollowLatest && visibleRange !== null) {
            this.scheduleScrollToLatest(visibleRange);
        }
    }

    get showPercentileControls() {
        return this.usesLogarithmicAverage(this.dataField) && this.groupingData === "5minutes";
    }

    /** True when every grouped bucket has percentile10/90 so L10/L90 series stay aligned */
    get percentilesAvailable(): boolean {
        if (!this.showPercentileControls || !this.deviceData?.length) {
            return false;
        }
        for (const d of this.deviceData) {
            if (!d.aggregation) {
                return false;
            }
            const p90 = d.aggregation.percentile90;
            const p10 = d.aggregation.percentile10;
            if (p90 == null || p10 == null || Number.isNaN(p90) || Number.isNaN(p10)) {
                return false;
            }
        }
        return true;
    }

    //Check if selected grouping requires aggregated chart series.
    private get groupData() {
        return this.groupingData === "5minutes";
    }

    //Clone chart configurations to avoid shared mutable references.
    private cloneChartConfig<T>(config: T): T {
        return JSON.parse(JSON.stringify(config));
    }

    private destroyRenderedChart() {
        const chart = this.chartRef as (HighchartsCore.Chart & { destroyed?: boolean }) | null;
        if (chart && !chart.destroyed) {
            chart.destroy();
        }
        this.chartRef = null;
    }

    //Recreate the chart component after structural changes.
    private recreateChart() {
        this.cancelScrollToLatest();

        //Clear current chart bindings.
        this.chartRef = null;
        this.showChart = false;
        this.activeChartConfig = null;
        this.cdr.detectChanges();

        //Mount a fresh chart instance.
        this.showChart = true;
        this.cdr.detectChanges();
    }

    private getVisibleRange(): number | null {
        const extremes = this.chartRef?.xAxis?.[0]?.getExtremes();
        if (typeof extremes?.min !== "number" || typeof extremes?.max !== "number") {
            return null;
        }

        return extremes.max - extremes.min;
    }

    private shouldFollowLatestPoint(): boolean {
        if (!this.liveData || !this.chartRef?.xAxis?.length) {
            return false;
        }

        const extremes = this.chartRef.xAxis[0].getExtremes();
        if (typeof extremes?.max !== "number" || typeof extremes?.dataMax !== "number") {
            return false;
        }

        const tolerance = this.getNavigatorFollowTolerance(extremes);
        return extremes.max >= extremes.dataMax - tolerance;
    }

    private getNavigatorFollowTolerance(extremes: HighchartsCore.ExtremesObject): number {
        const visibleRange = typeof extremes.max === "number" && typeof extremes.min === "number" ? extremes.max - extremes.min : 0;
        return Math.max(1000, visibleRange * 0.02);
    }

    private scheduleScrollToLatest(visibleRange: number) {
        this.cancelScrollToLatest();
        this.scrollToLatestTimeoutId = setTimeout(() => {
            this.scrollToLatestTimeoutId = null;
            this.scrollToLatest(visibleRange);
        }, 0);
    }

    private cancelScrollToLatest() {
        if (this.scrollToLatestTimeoutId === null) {
            return;
        }

        clearTimeout(this.scrollToLatestTimeoutId);
        this.scrollToLatestTimeoutId = null;
    }

    private scrollToLatest(visibleRange: number) {
        const axis = this.chartRef?.xAxis?.[0];
        const extremes = axis?.getExtremes();
        if (!axis || typeof extremes?.dataMax !== "number") {
            return;
        }

        const dataMax = extremes.dataMax;
        const newMin = Math.max((extremes.dataMin ?? dataMax) as number, dataMax - visibleRange);
        axis.setExtremes(newMin, dataMax, true, false);
    }

    public downloadDataExport() {
        if (this.exporting) {
            return;
        }

        this.exporting = true;

        const groupData = this.groupingDataExport === "5minutes";

        const startTime = moment(this.startTimeExport, "HH:mm");
        const startDate = this.startDateExport.startOf("hour");
        startDate.hour(startTime.get("hour"));
        startDate.minute(startTime.get("minutes"));

        const endTime = moment(this.endTimeExport, "HH:mm");
        const endDate = this.endDateExport.startOf("hour");
        endDate.hour(endTime.get("hour"));
        endDate.minute(endTime.get("minutes"));

        this.dataService.getDeviceDataRange(this.device.id, [this.dataFieldExport], startDate, endDate, groupData).subscribe(
            (data) => {
                const deviceDataExport = data[this.dataFieldExport];
                const exportData: unknown[][] = [];
                let headers: string[] | undefined;
                // From API data are coming from the most recent to the latest, reverting the order.
                deviceDataExport.sort((d1, d2) => new Date(d1.date).getTime() - new Date(d2.date).getTime());

                deviceDataExport.forEach((data) => {
                    // When grouping data use the average
                    if (groupData) {
                        exportData.push([
                            this.formatCsvTimestamp(data.date),
                            data.aggregation.average,
                            data.aggregation.percentile90,
                            data.aggregation.percentile10,
                        ]);
                        headers = ["timestamp", "value", "l10", "l90"];
                    } else {
                        const pointData = data.data ? data.data : [];
                        pointData.sort((d1, d2) => new Date(d1.timestamp).getTime() - new Date(d2.timestamp).getTime());
                        pointData.forEach((pointData) => {
                            const d = moment(pointData.timestamp);
                            if (d.isSameOrBefore(endDate) && d.isSameOrAfter(startDate)) {
                                if (this.dataFieldExport === DataTypeModelKey.spectrum13Octave) {
                                    exportData.push([
                                        this.formatCsvTimestamp(pointData.timestamp),
                                        ...(Array.isArray(pointData.value) ? pointData.value : [pointData.value]),
                                    ]);
                                } else {
                                    exportData.push([this.formatCsvTimestamp(pointData.timestamp), pointData.value]);
                                }
                            }
                        });
                        if (this.dataFieldExport === DataTypeModelKey.spectrum13Octave) {
                            headers = [
                                "timestamp",
                                "20 Hz",
                                "25 Hz",
                                "31.5 Hz",
                                "40 Hz",
                                "50 Hz",
                                "63 Hz",
                                "80 Hz",
                                "100 Hz",
                                "125 Hz",
                                "160 Hz",
                                "200 Hz",
                                "250 Hz",
                                "315 Hz",
                                "400 Hz",
                                "500 Hz",
                                "630 Hz",
                                "800 Hz",
                                "1 kHz",
                                "1.25 kHz",
                                "1.6 kHz",
                                "2 kHz",
                                "2.5 kHz",
                                "3.150 kHz",
                                "4 kHz",
                                "5 kHz",
                                "6.3 kHz",
                                "8 kHz",
                                "10 kHz",
                                "12.5 kHz",
                                "16 kHz",
                                "20 kHz",
                                "A",
                                "C",
                                "Z",
                            ];
                        } else {
                            headers = ["timestamp", "value"];
                        }
                    }
                });

                const csvOptions = {
                    fieldSeparator: ",",
                    quoteStrings: '"',
                    decimalseparator: ".",
                    showLabels: true,
                    headers: headers ?? [],
                };

                new AngularCsv(exportData, "GraphData-" + new Date().getTime(), csvOptions);

                this.exporting = false;
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
                if (error.error.statusCode === 404) {
                    this.router.navigate(["/devices"]);
                }
                this.exporting = false;
            },
        );
    }

    private adjustPlotGaps() {
        //We set the gap to data frequency for instant charts (plus 25% to avoid small date deviations)
        this.chartsConfig[0].options.plotOptions.line.gapSize = this.device.dataFrequency * 1000 * 2;
        this.chartsConfig[2].options.plotOptions.line.gapSize = this.device.dataFrequency * 1000 * 2;
        this.chartsConfig[4].options.plotOptions.line.gapSize = this.device.dataFrequency * 1000 * 2;
    }

    private formatCsvTimestamp(value: Date | string): string {
        const date = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value ? String(value) : "";
        }

        const outputFormat = this.toMomentDateFormat(this.project?.dateformat || "YYYY-MM-DD HH:mm:ss");
        const timezone = this.project?.timezone;
        const zoned = timezone ? moment(date).tz(timezone) : moment(date);
        const formatted = zoned.format(outputFormat);

        return formatted === "Invalid date" ? date.toISOString() : formatted;
    }

    private toMomentDateFormat(format: string): string {
        return format.replace(/yyyy/g, "YYYY").replace(/yy/g, "YY").replace(/dd/g, "DD").replace(/\ba\b/g, "A");
    }
}
